import { useCallback, useMemo, useState } from 'react';
import { useCuratedRoles } from 'stores/rq/roles';
import type { TeamRoleAssignmentRow } from 'components/common/role-assignments/types';
import type { Role } from 'types/api.types';
import type { RolesApi } from '@env0/role-service/api';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';

const getInitialRole = (defaultRoles: Role[], allCustomRoles: Role[]) => {
  if (defaultRoles.length) return defaultRoles[0].id!;
  if (allCustomRoles.length) return allCustomRoles[0].id!;

  return undefined;
};

export const useTeamRoleAssignmentRows = (defaultRoles: Role[], scopeId: string | undefined) => {
  const { allCustomRoles } = useCuratedRoles();
  const [rows, setRows] = useState<TeamRoleAssignmentRow[]>([]);

  // A card can stay mounted while its project or environment changes, and team ids repeat across scopes, so a new
  // scope starts a fresh draft rather than carrying edits over through refreshRows.
  const [draftScopeId, setDraftScopeId] = useState(scopeId);
  if (draftScopeId !== scopeId) {
    setDraftScopeId(scopeId);
    setRows([]);
  }

  const mergeRow = useCallback(
    (teamId: string, newData: Partial<TeamRoleAssignmentRow>) =>
      setRows(
        rows.map(row =>
          row.id === teamId
            ? {
                ...row,
                ...newData
              }
            : row
        )
      ),
    [rows, setRows]
  );

  // Reloaded rows keep the edits made on the previous ones, so loading more teams never discards unsaved changes.
  const refreshRows = useCallback(
    (nextRows: TeamRoleAssignmentRow[]) =>
      setRows(previousRows => {
        const edited = keyBy(
          previousRows.filter(row => row.assigned !== row.initialData.assigned || row.role !== row.initialData.role),
          'id'
        );
        return nextRows.map(row =>
          edited[row.id] ? { ...row, assigned: edited[row.id].assigned, role: edited[row.id].role } : row
        );
      }),
    []
  );

  const discardChanges = useCallback(
    () =>
      setRows(previousRows =>
        previousRows.map(row => ({ ...row, assigned: row.initialData.assigned, role: row.initialData.role }))
      ),
    []
  );

  const onChangeIsAssigned = useCallback(
    (teamId: string, assigned: boolean) => {
      const role = assigned ? getInitialRole(defaultRoles, allCustomRoles) : undefined;

      return mergeRow(teamId, { assigned, role });
    },
    [defaultRoles, allCustomRoles, mergeRow]
  );

  const onChangeAssignmentRole = useCallback(
    (teamId: string, role: RolesApi.RBACPermissionRole) => mergeRow(teamId, { role }),
    [mergeRow]
  );

  const rowsToRemove = useMemo(
    () =>
      filter(rows, {
        assigned: false,
        initialData: {
          assigned: true
        }
      }),
    [rows]
  );

  const rowsToAssign = useMemo(
    () =>
      filter(rows, {
        assigned: true,
        initialData: {
          assigned: false
        }
      }).filter(row => !!row.role),
    [rows]
  );

  const rowsToUpdate = useMemo(
    () =>
      filter(rows, {
        assigned: true,
        initialData: {
          assigned: true
        }
      }).filter(row => row.initialData.role !== row.role),
    [rows]
  );

  const hasChanges = rowsToRemove.length + rowsToAssign.length + rowsToUpdate.length > 0;

  return {
    rows,
    setRows,
    refreshRows,
    discardChanges,
    mergeRow,
    onChangeIsAssigned,
    onChangeAssignmentRole,
    rowsToRemove,
    rowsToAssign,
    rowsToUpdate,
    hasChanges
  };
};
